import { action } from "mobx";

import { StorageProperties } from "@libs/constants/storage-properties.ts";
import { DockableClinicalTab } from "@libs/gateways/clinical/ClinicalGateway.dtos.ts";
import { AustralianState } from "@libs/gateways/practice/PracticeGateway.dtos.ts";
import { IRootStore } from "@shared-types/root/root-store.interface.ts";
import { patientTabKey } from "@stores/clinical/utils/clinical.utils.ts";

import { ClinicalRecord } from "../ClinicalRecord.ts";
import { ClinicalTabItem } from "./ClinicalTabItem.ts";
import { ClinicalTabName } from "./ClinicalTabName.ts";
import { PatientClinicalRecordTab } from "./PatientClinicalRecordTab.ts";

export class ClinicalTabs {
  constructor(private root: IRootStore) {}

  private _clinicalTabName: ClinicalTabName;
  private _patientClinicalTabs = new Map<string, PatientClinicalRecordTab>();

  private getKey = (patientId: string, encounterId: string | undefined) => {
    const tabKey = !encounterId
      ? patientId
      : patientTabKey(patientId, encounterId);

    return `${tabKey}${!encounterId ? "-view" : ""}`;
  };

  @action
  public removePatientTabs = (
    patientId: string,
    encounterId: string | undefined
  ) => {
    const key = this.getKey(patientId, encounterId);

    const patientClinicalTab = this._patientClinicalTabs.get(key);
    if (patientClinicalTab) {
      patientClinicalTab.disposeReaction();
      this._patientClinicalTabs.delete(key);

      // remove tab from sessionStorage
      const clinicalRecordsState = sessionStorage.getItem(
        StorageProperties.clinicalRecordsState
      );

      if (clinicalRecordsState) {
        const clinicalRecordsObj: Map<string, AustralianState> =
          JSON.parse(clinicalRecordsState);

        const clinicalRecordsMap = new Map(clinicalRecordsObj);
        clinicalRecordsMap.delete(key);

        sessionStorage.setItem(
          StorageProperties.clinicalRecordsState,
          JSON.stringify(Array.from(clinicalRecordsMap))
        );
      }
    }
  };

  getPatientRecordTab = (clinicalRecord: ClinicalRecord) => {
    const key = this.getKey(clinicalRecord.id, clinicalRecord.encounterId);

    let tabs = this._patientClinicalTabs.get(key);

    if (!tabs) {
      tabs = new PatientClinicalRecordTab(this.root, clinicalRecord);
      this._patientClinicalTabs.set(key, tabs);
    }

    return tabs;
  };

  @action
  createClinicalTabItem(data: {
    type: DockableClinicalTab;
    encounterId?: string;
    title?: string;
    toolTipText?: string;
    contextId?: string;
  }): ClinicalTabItem {
    const tab = new ClinicalTabItem(data);

    // @TODO not clear for me why do we need it? need to ask Andrew. Ilya S.
    if (tab.encounterId && tab.hasGenericTitle) {
      if (!this._clinicalTabName)
        this._clinicalTabName = new ClinicalTabName(this.root.clinical);

      this._clinicalTabName
        .getName(data.type, tab.id)
        .then(data => tab.updateTitle(data));
    }

    return tab;
  }
}
